import { Injectable } from '@angular/core';
import { RegistroAvaliacao, ItemAvaliavel } from '../../../core/config/services/avaliacao.service';
import { EnturmacaoCompleta, LinhaDesempenho, ItemEducacional } from '../models/nota-types';

/**
 * Serviço responsável por transformações e normalizações de dados relacionados a notas
 * Separação de responsabilidades do NotaService
 */
@Injectable({
  providedIn: 'root'
})
export class NotaTransformerService {
  private getNotaConceitoText(value: RegistroAvaliacao['notaConceito']): string {
    if (typeof value === 'string') {
      return value;
    }

    if (value && typeof value === 'object') {
      const sigla = value.sigla;
      if (typeof sigla === 'string') {
        return sigla;
      }
    }

    return '';
  }

  private normalizeNotaConceitoValue(value: RegistroAvaliacao['notaConceito']): { sigla?: string; descricao?: string; [key: string]: unknown } | string | null {
    if (!value) {
      return null;
    }

    if (typeof value === 'string') {
      return this.sanitizeString(value);
    }

    if (typeof value === 'object') {
      const sigla = typeof value.sigla === 'string' ? this.sanitizeString(value.sigla) : undefined;
      const descricao = typeof value.descricao === 'string' ? this.sanitizeString(value.descricao) : undefined;

      return {
        ...value,
        ...(sigla ? { sigla } : {}),
        ...(descricao ? { descricao } : {})
      };
    }

    return null;
  }

  /** Remove tags HTML e limita tamanho. */
  sanitizeString(value: string): string {
    if (!value || typeof value !== 'string') {
      return '';
    }
    return value
      .replace(/<[^>]*>/g, '')
      .substring(0, 16000)
      .trim();
  }

  /**
   * Normaliza estrutura da enturmação garantindo que tenha matrícula aninhada
   */
  normalizarEnturmacao(enturmacao: EnturmacaoCompleta): EnturmacaoCompleta {
    if (enturmacao.matricula) {
      if (!enturmacao.matricula.foto && enturmacao.foto) {
        return {
          ...enturmacao,
          matricula: {
            ...enturmacao.matricula,
            foto: enturmacao.foto
          }
        };
      }
      return enturmacao;
    }

    if (enturmacao.nome) {
      const fotoValue = enturmacao.foto || 
        (enturmacao as { matricula?: { foto?: string | { id?: number; src?: string } } }).matricula?.foto;
      
      return {
        ...enturmacao,
        matricula: {
          id: enturmacao.id ?? enturmacao.matriculaId ?? 0,
          nome: enturmacao.nome ?? '',
          situacao: enturmacao.situacao || 'MATRICULADO',
          foto: fotoValue as string | { id?: number; src?: string } | undefined
        }
      };
    }

    return enturmacao;
  }

  /**
   * Define a situação da enturmação seguindo ordem de prioridade
   */
  definirSituacaoEnturmacao(
    linha: LinhaDesempenho,
    enturmacaoNormalizada: EnturmacaoCompleta,
    itemEducacional: ItemEducacional
  ): void {
    // Ordem de prioridade: situação do item educacional > enturmação > matrícula > fallback 'MATRICULADO'.
    if (enturmacaoNormalizada.situacaoItensEducacionais && itemEducacional) {
      const situacaoItem = enturmacaoNormalizada.situacaoItensEducacionais.find((sit) =>
        sit.itemEducacional?.id === itemEducacional.id
      );
      if (situacaoItem?.situacao) {
        linha.enturmacao.situacao = situacaoItem.situacao;
      }
    }

    if (!linha.enturmacao.situacao) {
      linha.enturmacao.situacao = enturmacaoNormalizada.situacao;
    }

    if (!linha.enturmacao.situacao) {
      linha.enturmacao.situacao = enturmacaoNormalizada.matricula?.situacao;
    }

    if (!linha.enturmacao.situacao) {
      linha.enturmacao.situacao = 'MATRICULADO';
    }

    if (!linha.enturmacao.matricula) {
      linha.enturmacao.matricula = {
        id: enturmacaoNormalizada.matricula?.id || 0,
        nome: enturmacaoNormalizada.matricula?.nome || '',
        situacao: linha.enturmacao.situacao
      };
    } else if (!linha.enturmacao.matricula.situacao) {
      linha.enturmacao.matricula.situacao = linha.enturmacao.situacao;
    }
  }

  /**
   * Obtém o valor da nota baseado no modo de avaliação
   * Valida e sanitiza os valores retornados
   */
  getNotaValue(registro: RegistroAvaliacao): string | number | { sigla?: string; descricao?: string; [key: string]: unknown } | null {
    const modoAvaliacao = registro.avaliavel?.modoAvaliacao;
    
    switch (modoAvaliacao) {
      case 'NUMERICO': {
        const notaNumerica = registro.notaNumerica;
        if (notaNumerica !== null && notaNumerica !== undefined) {
          if (notaNumerica >= 0 && notaNumerica <= 100) {
            return notaNumerica;
          }
        }
        return null;
      }
      case 'CONCEITO':
      case 'CONCEITO_NUMERICO':
        return this.normalizeNotaConceitoValue(registro.notaConceito);
      case 'DESCRITIVO':
        return registro.notaDescritiva ? this.sanitizeString(registro.notaDescritiva) : null;
      default:
        if (registro.notaNumerica !== null && registro.notaNumerica !== undefined) {
          return registro.notaNumerica;
        }
        if (registro.notaConceito) {
          return this.normalizeNotaConceitoValue(registro.notaConceito);
        }
        if (registro.notaDescritiva) {
          return this.sanitizeString(registro.notaDescritiva);
        }
        return null;
    }
  }

  filtrarItensAvaliaveisParaColunas(itensAvaliaveis: ItemAvaliavel[], porInstrumento: boolean): ItemAvaliavel[] {
    if (porInstrumento) {
      return itensAvaliaveis.filter(item =>
        item.tipoPeriodo === 'INSTRUMENTO' || item.tipoPeriodo === 'CALCULO_INSTRUMENTOS'
      );
    } else {
      // Por ano: mostra períodos avaliativos, excluindo os tipos de instrumento.
      const tiposExcluidos = [
        'INSTRUMENTO',
        'CALCULO_INSTRUMENTOS',
        'CHA_OBJETIVO_MEDIA_PERIODO',
        'CHA_OBJETIVO_EXAME_FINAL'
      ];
      return itensAvaliaveis.filter(item => !tiposExcluidos.includes(item.tipoPeriodo));
    }
  }
}

